
import React, { lazy } from 'react';
import { RouteObject } from 'react-router-dom';
import AuthProtectedRoute from './components/AuthProtectedRoute';

// Route components are lazy-loaded so each page ships as its own chunk, keeping the initial
// bundle small. A <Suspense> boundary in App.tsx renders a spinner while a chunk loads.
const Index = lazy(() => import('./pages/Index'));
const HyperframesShowcase = lazy(() => import('./pages/HyperframesShowcase'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Events = lazy(() => import('./pages/Events'));
const AddEvent = lazy(() => import('./pages/AddEvent'));
const EditEvent = lazy(() => import('./pages/EditEvent'));
const ViewEvent = lazy(() => import('./pages/ViewEvent'));
const AddProgram = lazy(() => import('./pages/AddProgram'));
const EditProgram = lazy(() => import('./pages/EditProgram'));
const Analytics = lazy(() => import('./pages/Analytics'));
const TeamManagement = lazy(() => import('./pages/TeamManagement'));
const TeamMembers = lazy(() => import('./pages/TeamMembers'));
const AddTeam = lazy(() => import('./pages/AddTeam'));
const EditTeam = lazy(() => import('./pages/EditTeam'));
const AdminUserManagementPage = lazy(() => import('./pages/AdminUserManagementPage'));
const ArticleLayout = lazy(() => import('./components/program/details/article/ArticleLayout'));
const PublicEventProgram = lazy(() => import('./pages/PublicEventProgram'));
const PublicProgramItem = lazy(() => import('./pages/PublicProgramItem'));
const Login = lazy(() => import('./pages/Login'));
const Signup = lazy(() => import('./pages/Signup'));
const VerificationPending = lazy(() => import('./pages/VerificationPending'));
const Logout = lazy(() => import('./pages/Logout'));
const PasswordRecovery = lazy(() => import('./pages/PasswordRecovery'));
const Profile = lazy(() => import('./pages/Profile'));
const ChangePassword = lazy(() => import('./pages/ChangePassword'));
const ChangeEmail = lazy(() => import('./pages/ChangeEmail'));
const Terms = lazy(() => import('./pages/Terms'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Cookies = lazy(() => import('./pages/Cookies'));
const UserDetailsPage = lazy(() => import('./pages/UserDetailsPage'));
const Search = lazy(() => import('./pages/Search'));
const LicenseKeysManagement = lazy(() => import('./pages/LicenseKeysManagement'));
const Subscription = lazy(() => import('./pages/Subscription'));
const Library = lazy(() => import('./pages/Library'));
const Activate = lazy(() => import('./pages/Activate'));
const AdminSuggestedProductsPage = lazy(() => import('./pages/AdminSuggestedProductsPage'));
const Discover = lazy(() => import('./pages/Discover'));
const ContentPage = lazy(() => import('./pages/ContentPage'));
const Pricing = lazy(() => import('./pages/Pricing'));

// Wrap components with AuthProtectedRoute for authenticated routes
const protectedRoute = (element: React.ReactNode) => (
  <AuthProtectedRoute>{element}</AuthProtectedRoute>
);

export const routes: RouteObject[] = [
  {
    path: '/',
    element: <Index />,
  },
  {
    path: '/hyperframes',
    element: <HyperframesShowcase />,
  },
  {
    // Public content hub: crawlable directory of published events (de-orphans the library).
    path: '/discover',
    element: <Discover />,
  },
  {
    path: '/login',
    element: <Login />,
  },
  {
    path: '/signup',
    element: <Signup />,
  },
  {
    path: '/verification-pending',
    element: <VerificationPending />,
  },
  {
    path: '/logout',
    element: <Logout />,
  },
  {
    path: '/login/recover',
    element: <PasswordRecovery />,
  },
  {
    // Password-reset emails link here with ?token=; must work logged-out.
    path: '/change-password',
    element: <ChangePassword />,
  },
  {
    // Platform-migration activation emails land here with ?token=.
    path: '/activate',
    element: <Activate />,
  },
  {
    path: '/dashboard',
    element: protectedRoute(<Dashboard />),
  },
  {
    path: '/events',
    element: protectedRoute(<Events />),
  },
  {
    path: '/events/add',
    element: protectedRoute(<AddEvent />),
  },
  {
    path: '/events/edit/:id',
    element: protectedRoute(<EditEvent />),
  },
  {
    path: '/events/view/:id',
    element: protectedRoute(<ViewEvent />),
  },
  {
    path: '/events/program/add/:eventId',
    element: protectedRoute(<AddProgram />),
  },
  {
    path: '/events/program/edit/:id',
    element: protectedRoute(<EditProgram />),
  },
  {
    path: '/events/program/view/:id/*',
    element: protectedRoute(<ArticleLayout />),
  },
  {
    path: '/events/publish/:id',
    element: <PublicEventProgram />,
  },
  {
    path: '/events/publish/:eventId/program/:programId',
    element: <PublicProgramItem />,
  },
  {
    path: '/analytics',
    element: protectedRoute(<Analytics />),
  },
  {
    path: '/teams',
    element: protectedRoute(<TeamManagement />),
  },
  {
    path: '/teams/add',
    element: protectedRoute(<AddTeam />),
  },
  {
    path: '/teams/edit/:id',
    element: protectedRoute(<EditTeam />),
  },
  {
    path: '/teams/members/:id',
    element: protectedRoute(<TeamMembers />),
  },
  {
    path: '/users',
    element: protectedRoute(<AdminUserManagementPage />),
  },
  {
    path: '/users/:id',
    element: protectedRoute(<UserDetailsPage />),
  },
  {
    path: '/profile',
    element: protectedRoute(<Profile />),
  },
  {
    path: '/profile/password',
    element: protectedRoute(<ChangePassword />),
  },
  {
    path: '/profile/change_email',
    element: protectedRoute(<ChangeEmail />),
  },
  {
    path: '/search',
    element: protectedRoute(<Search />),
  },
  {
    path: '/subscription',
    element: protectedRoute(<Subscription />),
  },
  {
    path: '/license-keys',
    element: protectedRoute(<LicenseKeysManagement />),
  },
  {
    path: '/library',
    element: protectedRoute(<Library />),
  },
  {
    path: '/admin/suggested-products',
    element: protectedRoute(<AdminSuggestedProductsPage />),
  },
  { path: '/about', element: <ContentPage /> },
  // Server-rendered content pages (marketing, glossary, blog, tools). ContentPage
  // derives its slug from the path and fetches /api/page; content lives in server/content.
  { path: '/product', element: <ContentPage /> },
  { path: '/pricing', element: <Pricing /> },
  { path: '/use-cases', element: <ContentPage /> },
  { path: '/use-cases/:sub', element: <ContentPage /> },
  { path: '/glossary', element: <ContentPage /> },
  { path: '/glossary/:term', element: <ContentPage /> },
  { path: '/blog', element: <ContentPage /> },
  { path: '/blog/:post', element: <ContentPage /> },
  { path: '/tools', element: <ContentPage /> },
  { path: '/tools/:tool', element: <ContentPage /> },
  {
    path: '/terms',
    element: <Terms />,
  },
  {
    path: '/privacy',
    element: <Privacy />,
  },
  {
    path: '/cookies',
    element: <Cookies />,
  },
  {
    path: '*',
    element: <NotFound />,
  },
];
